import {
  Action,
  Actions,
} from '@cotera/client/v0/components/ai-elements/actions';
import {
  Message,
  MessageContent,
} from '@cotera/client/v0/components/ai-elements/message';
import type {
  FilePart as FilePartModel,
  ReasoningPart as ReasoningPartModel,
  SourceUrlPart as SourceUrlPartModel,
  TextPart as TextPartModel,
} from '@cotera/sdk/core';
import { DG, MessageClass, ToolPart } from '@cotera/sdk/core';
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from '@cotera/client/v0/components/ai-elements/reasoning';
import { Response } from '@cotera/client/v0/components/ai-elements/response';
import {
  Source,
  Sources,
  SourcesContent,
  SourcesTrigger,
} from '@cotera/client/v0/components/ai-elements/sources';
import {
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
  ToolOutput,
} from '@cotera/client/v0/components/ai-elements/tool';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@cotera/client/v0/components/ui/collapsible';
import { Icon } from '@cotera/client/app/components/ui';
import { useAgentDisplayNameResolver } from '@cotera/client/app/hooks/use-agents-display-names';
import { useSuspensfulToolInfo } from '@cotera/client/app/hooks/use-tools-info';
import { coco } from '@cotera/api';
import { formatLocalRelativeShort } from '@cotera/utilities';
import { PROVIDER_ICONS } from '@cotera/client/v0/components/tool-connections/tool-connections-dialog';
import { Alert, AlertDescription } from '@cotera/client/v0/components/ui/alert';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Textarea } from '@cotera/client/v0/components/ui/textarea';
import {
  ArrowDownToLine,
  Check,
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  LoaderIcon,
  Pencil,
  RefreshCcwIcon,
  X,
  XIcon,
  PaperclipIcon,
} from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { serializeEditorStateForClipboard } from '@cotera/client/v0/components/ai-elements/readonly-editor-extensions';
import { useAction, useWatchableValue, Watchable } from '../actions/framework';
import { cn } from '../lib/utils';
import { EditAction } from './actions/edit.action';
import { ExecuteToolAction } from './actions/execute-tool.action';
import { ReplayAction } from './actions/replay.action';
import { ChatModel } from './chat.model';
import { ArtifactSnippet } from './artifact-snippet';
import { ArtifactEditIndicator } from './artifact-edit-indicator';
import { useArtifactInlineDisplayMode } from './artifact-inline-dedupe-context';
import { ToolOutputViewer } from './tool-output-viewer';
import {
  getCustomToolRenderer,
  type CustomToolRenderer,
} from './tool-renderers';
import { HijackerQueueModel } from './hijacker-queue.model';
import { useInScopeModel } from '@cotera/client/v0/actions/framework';
import { ReadonlyEditorState } from '@cotera/client/v0/components/ai-elements/readonly-editor-state';
import type { MentionClickData } from '@cotera/client/v0/components/ai-elements/readonly-editor-state';
import {
  useAnalytics,
  useCocoConversationId,
  useChatSurface,
  CocoEvent,
} from '@cotera/client/app/etc';

const { isAgentToolKey } = coco;

export type MessageActionsConfig = {
  showReplay?: boolean;
  showEdit?: boolean;
  /** When provided, mention chips in editor state parts are clickable */
  onMentionClick?: (data: MentionClickData) => void;
};

const MessageTimestamp = ({ message }: { message: MessageClass }) => (
  <span
    className="px-1 text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
    title={message.createdAt.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'long',
    })}
  >
    {formatLocalRelativeShort(message.createdAt)}
  </span>
);

// Component for rendering sources
const MessageSources = ({ message }: { message: MessageClass }) => {
  const sourceParts = message.parts.filter((part): part is SourceUrlPartModel =>
    part.isSourceUrlPart()
  );

  if (!message.isAssistantMessage() || sourceParts.length === 0) {
    return null;
  }

  return (
    <Sources>
      <SourcesTrigger count={sourceParts.length} />
      {sourceParts.map((part, i) => (
        <SourcesContent key={`${message.id}-${i}`}>
          <Source href={part.url} title={part.title ?? part.url} />
        </SourcesContent>
      ))}
    </Sources>
  );
};

export type ApprovalHandler = (params: {
  toolCallId: string;
  approve: boolean;
  reason?: string;
}) => Promise<void>;

const DEFAULT_HIJACKLER_TOOL_IDS = Watchable.fromValue<Set<string>>(new Set());

/** True when tool output is shaped like a failure (matches ToolOutputViewer error UI). */
function toolOutputIndicatesExecutionFailure(output: unknown): boolean {
  if (output === null || output === undefined || typeof output !== 'object') {
    return false;
  }
  const o = output as Record<string, unknown>;
  if (o['type'] === 'execution-error') {
    return true;
  }
  if (typeof o['error'] === 'string' && o['error'].length > 0) {
    return true;
  }
  return false;
}

/**
 * Header state for ai-elements ToolHeader: map success-shaped parts that actually
 * carry errors (e.g. { error: "..." }) to output-error so the icon matches the body.
 */
function getEffectiveToolHeaderState(part: ToolPart): string {
  if (part.isIncomplete()) {
    return 'input-available';
  }
  const raw = part.toolPart.state;
  if (raw === 'output-error' || raw === 'output-denied') {
    return raw;
  }
  const errorText = (part.toolPart as { errorText?: string | undefined })
    .errorText;
  if (typeof errorText === 'string' && errorText !== '') {
    return 'output-error';
  }
  if (toolOutputIndicatesExecutionFailure(part.getOutput())) {
    return 'output-error';
  }
  return raw;
}

/**
 * The per-call row title. `coco/ingot-query` carries a short `description` of
 * what this particular search is for. We prefix it with the static tool name
 * ("Search chat memory") so each memory lookup reads as a memory search rather
 * than masquerading as whatever the description happens to mention (e.g. a bare
 * "stripe invoice metadata" that looks like Coco ran a Stripe tool unprompted),
 * while still surfacing what the lookup is for.
 */
function toolCallTitle(part: ToolPart, fallback: string): string {
  if (part.toolKey().toString() === coco.COCO_INGOT_QUERY_TOOL_KEY) {
    const description = part.input['description'];
    if (typeof description === 'string' && description.trim() !== '') {
      return `${fallback}: ${description.trim()}`;
    }
  }
  return fallback;
}

// Passed-in renderers win over the v0 registry, matching ToolPartComponent.
const resolveToolRenderer = (
  toolName: string,
  customToolRenderers?:
    | Record<string, CustomToolRenderer>
    | ((toolKey: string) => CustomToolRenderer | null)
): CustomToolRenderer | null => {
  const passed =
    customToolRenderers !== undefined
      ? typeof customToolRenderers === 'function'
        ? customToolRenderers(toolName)
        : customToolRenderers[toolName] ?? null
      : null;
  return passed ?? getCustomToolRenderer(toolName);
};

// Custom-rendered tools that should still collapse into a grouped row when they
// appear back-to-back. Generic (registry-less) tools always group; these opt in
// despite having a custom renderer. The renderer still draws each call's body —
// the group only adds the run-count badge and the per-run list.
const GROUPABLE_CUSTOM_TOOL_KEYS = new Set<string>([
  coco.COCO_SEARCH_TOOL_KEY,
  'coco/search',
  coco.COCO_WEB_SEARCH_TOOL_KEY,
]);

// Workspace-editing / sub-agent tools also collapse when repeated. Some of these
// keys are static; agent tools use a dynamic `coco/agent-<uuid>` key, so we match
// by predicate rather than a Set. Mirrors isSubAgentLikeToolKey in
// v1-tool-renderers so the grouped list and the custom renderer agree.
const WORKSPACE_EDIT_TOOL_KEYS = new Set<string>([
  coco.COCO_CREATE_AGENT_TOOL_KEY,
  coco.COCO_EDIT_AGENT_TOOL_KEY,
  coco.COCO_AGENT_EDITOR_TOOL_KEY,
  coco.COCO_DOCUMENT_EDITOR_TOOL_KEY,
  coco.COCO_FOLDER_EDITOR_TOOL_KEY,
  coco.COCO_COTERA_WORKSPACE_TOOL_KEY,
  'coco/sub-agent',
  'coco/cotera-agent',
]);

// Whether a custom-rendered tool key opts into grouping. Handles both slash
// (internal) and underscore (LLM-facing) key formats.
const isGroupableCustomToolKey = (key: string): boolean => {
  const normalized = key.replaceAll('_', '/');
  return (
    GROUPABLE_CUSTOM_TOOL_KEYS.has(key) ||
    WORKSPACE_EDIT_TOOL_KEYS.has(normalized) ||
    isAgentToolKey(normalized)
  );
};

// Extract provider from tool name.
// Handles formats: provider/group/action (slash) or provider_action (underscore)
const getProviderFromToolName = (name: string): string => {
  const firstSlash = name.indexOf('/');
  if (firstSlash > 0) {
    return name.substring(0, firstSlash);
  }
  const firstUnderscore = name.indexOf('_');
  if (firstUnderscore > 0) {
    return name.substring(0, firstUnderscore);
  }
  return name;
};

// The tool's declared provider wins over the one implied by its key. They agree
// for most tools, but a Coco tool's key namespaces it under `coco/` rather than
// under what it acts on — so `coco/dataset-read` would otherwise draw the
// generic Cotera mark instead of the dataset icon its provider asks for. The key
// stays the fallback for tools the list has not loaded yet. Returns undefined so
// ToolHeader can fall back to its default wrench.
const renderToolIcon = (
  toolName: string,
  toolInfo: { provider?: string } | null | undefined
): ReactNode | undefined => {
  const provider = toolInfo?.provider ?? getProviderFromToolName(toolName);
  const ProviderIcon = PROVIDER_ICONS[provider];
  if (isAgentToolKey(toolName)) {
    return (
      <span className="h-4 w-4 flex items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
        <Icon icon="bot" className="h-4 w-4" />
      </span>
    );
  }
  if (ProviderIcon !== undefined) {
    return (
      <span className="h-4 w-4 flex items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
        <ProviderIcon />
      </span>
    );
  }
  return undefined;
};

// The input/output body of a completed (non-approval) tool call. Shared by the
// single-call row and the grouped-call navigator.
const ToolCallOutput = ({ part }: { part: ToolPart }) => {
  const showInlineArtifactSnippet = useArtifactInlineDisplayMode(part);
  return (
    <ToolOutput
      errorText={part.toolPart.errorText}
      output={(() => {
        if (part.isIncomplete()) {
          return (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <LoaderIcon className="h-4 w-4 animate-spin" />
              <span>Waiting for tool to complete…</span>
            </div>
          );
        }

        const artifact = part.getArtifact();
        if (artifact !== null && showInlineArtifactSnippet === 'full') {
          const artifactId = artifact.artifactId ?? part.toolCallId;
          return (
            <ArtifactSnippet
              artifactId={artifactId}
              artifactType={artifact.artifactType}
              payload={artifact.data}
            />
          );
        }
        if (artifact !== null && showInlineArtifactSnippet === 'edit') {
          const artifactId = artifact.artifactId ?? part.toolCallId;
          return (
            <ArtifactEditIndicator
              artifactId={artifactId}
              artifactType={artifact.artifactType}
              payload={artifact.data}
            />
          );
        }
        return part.getOutput<any>() ? (
          <ToolOutputViewer value={part.getOutput<any>()} />
        ) : null;
      })()}
    />
  );
};

// Small status glyph for a single call inside the grouped navigator.
const ToolCallStatusIcon = ({ part }: { part: ToolPart }) => {
  if (part.isIncomplete()) {
    return (
      <LoaderIcon className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
    );
  }
  const state = getEffectiveToolHeaderState(part);
  if (state === 'output-error') {
    return <XIcon className="h-3.5 w-3.5 text-red-600" />;
  }
  if (state === 'output-denied') {
    return <XIcon className="h-3.5 w-3.5 text-orange-600" />;
  }
  return <CheckIcon className="h-3.5 w-3.5 text-success" />;
};

// Best-effort one-line summary of a call's input, used to tell repeated runs
// apart in the grouped list. Returns null when nothing concise is available.
const summarizeToolInput = (input: unknown): string | null => {
  if (typeof input === 'string') {
    return input.trim() === '' ? null : input;
  }
  if (typeof input === 'number' || typeof input === 'boolean') {
    return String(input);
  }
  if (input === null || typeof input !== 'object') {
    return null;
  }
  const obj = input as Record<string, unknown>;
  // Human-meaningful fields first, then fall back to the first primitive value.
  const preferred = [
    'name',
    'title',
    'query',
    'q',
    'search',
    'prompt',
    'question',
    'id',
    'slug',
    'url',
    'path',
    'key',
  ];
  const asString = (v: unknown): string | null => {
    if (typeof v === 'string') {
      return v.trim() === '' ? null : v;
    }
    if (typeof v === 'number' || typeof v === 'boolean') {
      return String(v);
    }
    return null;
  };
  for (const field of preferred) {
    const summary = asString(obj[field]);
    if (summary !== null) {
      return summary;
    }
  }
  for (const v of Object.values(obj)) {
    const summary = asString(v);
    if (summary !== null) {
      return summary;
    }
  }
  return null;
};

// One run inside a grouped tool row. Each run is independently collapsible so a
// user can see at a glance that N separate calls happened — and open any of them,
// not just the last.
const ToolCallGroupItem = ({
  part,
  index,
  chatModel,
  readonly,
  CustomRenderer,
}: {
  part: ToolPart;
  index: number;
  chatModel: Pick<ChatModel, 'addToolResult' | 'id' | 'model'>;
  readonly?: boolean;
  CustomRenderer: CustomToolRenderer | null;
}) => {
  const [open, setOpen] = useState(false);
  const summary = summarizeToolInput(part.input);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="border-l-2 border-border pl-3"
    >
      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 py-1.5 text-left">
        <div className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
          <ToolCallStatusIcon part={part} />
          <span className="shrink-0 font-medium">Run {index + 1}</span>
          {summary !== null ? (
            <span className="truncate text-xs opacity-70">{summary}</span>
          ) : null}
        </div>
        <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        {CustomRenderer !== null ? (
          <div className="pt-2">
            <CustomRenderer
              key={part.toolCallId}
              part={part}
              chatModel={chatModel}
              readonly={readonly}
              nested
            />
          </div>
        ) : (
          <>
            <ToolInput input={part.input} />
            <ToolCallOutput part={part} />
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

// Collapses a run of consecutive same-tool calls into one row with a "<N> runs"
// badge. Expanding lists every run vertically (each independently openable) so
// it's obvious multiple distinct calls happened — not just the last one.
const ToolPartGroupComponent = ({
  toolParts,
  chatModel,
  readonly,
  customToolRenderers,
}: {
  toolParts: ToolPart[];
  chatModel: Pick<ChatModel, 'addToolResult' | 'id' | 'model'>;
  readonly?: boolean;
  customToolRenderers?:
    | Record<string, CustomToolRenderer>
    | ((toolKey: string) => CustomToolRenderer | null);
}) => {
  const hijackerQueueModel = useInScopeModel(HijackerQueueModel);
  const hijackedToolCallIds = useWatchableValue(
    hijackerQueueModel?.hijackedToolCallIds ?? DEFAULT_HIJACKLER_TOOL_IDS
  );
  const visibleParts = toolParts.filter(
    (p) => hijackedToolCallIds?.has(p.toolCallId) !== true
  );

  const lastPart = visibleParts[visibleParts.length - 1];
  const toolName = lastPart?.toolKey().toString() ?? '';
  const getAgentDisplayName = useAgentDisplayNameResolver();
  const toolInfo = useSuspensfulToolInfo(toolName);

  const count = visibleParts.length;
  const [isExpanded, setIsExpanded] = useState(false);

  if (lastPart === undefined) {
    return null;
  }

  const toolKey = lastPart.toolKey();
  const toolTitle =
    getAgentDisplayName(toolName) ?? toolInfo?.name ?? toolKey.displayName();
  const toolIcon = renderToolIcon(toolName, toolInfo);
  const CustomRenderer = resolveToolRenderer(toolName, customToolRenderers);

  // Label the group by its most recent call — for coco/ingot-query that is the
  // latest search's description rather than the static tool name.
  const displayTitle = toolCallTitle(lastPart, toolTitle);

  const anyRunning = visibleParts.some((p) => p.isIncomplete());
  const anyError = visibleParts.some((p) => {
    const s = getEffectiveToolHeaderState(p);
    return s === 'output-error' || s === 'output-denied';
  });
  const aggregateState = anyRunning
    ? 'input-available'
    : anyError
    ? 'output-error'
    : 'output-available';

  return (
    <Tool open={isExpanded} onOpenChange={setIsExpanded}>
      <ToolHeader
        state={aggregateState}
        title={displayTitle}
        type={lastPart.toolPart.type}
        icon={toolIcon}
        badge={`${count} runs`}
      />
      <ToolContent>
        <div className="flex flex-col gap-1 px-4 pt-3">
          {visibleParts.map((part, index) => (
            <ToolCallGroupItem
              key={part.toolCallId}
              part={part}
              index={index}
              chatModel={chatModel}
              readonly={readonly}
              CustomRenderer={CustomRenderer}
            />
          ))}
        </div>
      </ToolContent>
    </Tool>
  );
};

// Component for rendering tool calls
const ToolPartComponent = ({
  toolPart,
  chatModel,
  readonly,
  customToolRenderers,
  approvalHandler,
}: {
  toolPart: ToolPart;
  chatModel: Pick<ChatModel, 'addToolResult' | 'id' | 'model'>;
  readonly?: boolean;
  customToolRenderers?:
    | Record<string, CustomToolRenderer>
    | ((toolKey: string) => CustomToolRenderer | null);
  /** When provided (e.g. in sub-agent context), used instead of ExecuteToolAction for approvals. */
  approvalHandler?: ApprovalHandler;
}) => {
  // Resolve the tool key/name once — toolKey() is memoized on the part, but the
  // .toString() and the downstream lookups run several times per render.
  const toolKey = toolPart.toolKey();
  const toolName = toolKey.toString();

  const hijackerQueueModel = useInScopeModel(HijackerQueueModel);
  const hijackedToolCallIds = useWatchableValue(
    hijackerQueueModel?.hijackedToolCallIds ?? DEFAULT_HIJACKLER_TOOL_IDS
  );
  const executeToolAction = useAction(ExecuteToolAction);
  const getAgentDisplayName = useAgentDisplayNameResolver();
  const toolInfo = useSuspensfulToolInfo(toolName);

  const part = toolPart;
  const approvalStatus = toolPart.approvalStatus();
  const requiresManualApproval = approvalStatus === 'required';

  const [localApprovalState, setLocalApprovalState] = useState<{
    approved?: boolean;
    reason?: string;
  } | null>(null);

  const [isExpanded, setIsExpanded] = useState(requiresManualApproval);

  useEffect(() => {
    if (requiresManualApproval && localApprovalState?.approved !== true) {
      setIsExpanded((prev) => (prev ? prev : true));
      return;
    }

    if (localApprovalState?.approved === true) {
      setIsExpanded((prev) => (prev ? false : prev));
    }
  }, [requiresManualApproval, localApprovalState?.approved]);

  if (hijackedToolCallIds?.has(toolPart.toolCallId) === true) {
    return null;
  }

  // Check for custom renderer - passed-in renderers take precedence, then v0 registry
  const resolvedRenderer = resolveToolRenderer(toolName, customToolRenderers);
  if (resolvedRenderer !== null) {
    const Renderer = resolvedRenderer;
    // Add key to force remount when toolCallId changes
    return (
      <div key={part.toolCallId} className="px-4">
        <Renderer
          key={part.toolCallId}
          part={part}
          chatModel={chatModel}
          readonly={readonly}
        />
      </div>
    );
  }

  const toolIcon = renderToolIcon(toolName, toolInfo);

  // Look up the correct conversation ID from the hijacker queue model
  // This is important for sub-agent tools that belong to a different conversation
  const conversationIdForTool =
    hijackerQueueModel?.getConversationIdForToolCall(part.toolCallId);

  const handleApprove = async () => {
    setLocalApprovalState({ approved: true });
    if (approvalHandler !== undefined) {
      await approvalHandler({
        toolCallId: part.toolCallId,
        approve: true,
      });
    } else {
      await executeToolAction.execute({
        toolCallId: part.toolCallId,
        approve: true,
        conversationId: conversationIdForTool,
      });
    }
  };

  const handleReject = async () => {
    const reason = 'User denied tool execution';
    setLocalApprovalState({ approved: false, reason });
    if (approvalHandler !== undefined) {
      await approvalHandler({
        toolCallId: part.toolCallId,
        approve: false,
        reason,
      });
    } else {
      await executeToolAction.execute({
        toolCallId: part.toolCallId,
        deny: true,
        reason,
        conversationId: conversationIdForTool,
      });
    }
  };

  const showApprovalPrompt =
    requiresManualApproval && localApprovalState === null;
  const notYetApproved = showApprovalPrompt;
  const approved = localApprovalState?.approved === true;
  const rejected = localApprovalState?.approved === false;
  // Dumb but the ai elements know how to handle this while the sdk does not.
  const toolHeaderState: any = requiresManualApproval
    ? notYetApproved
      ? 'approval-requested'
      : approved
      ? 'approval-responded'
      : rejected
      ? 'output-denied'
      : getEffectiveToolHeaderState(part)
    : getEffectiveToolHeaderState(part);
  const toolTitle = toolCallTitle(
    part,
    getAgentDisplayName(toolName) ?? toolInfo?.name ?? toolKey.displayName()
  );

  return (
    <Tool open={isExpanded} onOpenChange={setIsExpanded}>
      <ToolHeader
        state={toolHeaderState}
        title={toolTitle}
        type={part.toolPart.type}
        icon={toolIcon}
      />
      <ToolContent>
        <ToolInput input={part.input} />

        {/* Show approval UI for tools that need approval */}
        {showApprovalPrompt && !readonly && (
          <div className="px-4">
            <Alert className="flex flex-col gap-2 mb-4 bg-card/60">
              <AlertDescription className="inline">
                <div className="mb-2">
                  Tool <strong>{toolTitle}</strong> requires approval to
                  execute.
                </div>
                <div className="flex items-center justify-end gap-2 self-end">
                  <Button
                    className="h-8 px-3 text-sm"
                    type="button"
                    variant="outline"
                    onClick={handleReject}
                  >
                    Reject
                  </Button>
                  <Button
                    className="h-8 px-3 text-sm"
                    type="button"
                    variant="default"
                    onClick={handleApprove}
                  >
                    Accept
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Show approved state */}
        {localApprovalState?.approved === true && (
          <div className="px-4">
            <Alert className="flex flex-col gap-2 border-success/30 bg-success/5">
              <AlertDescription className="flex items-center gap-2">
                <CheckIcon className="size-4 text-success" />
                <span>Tool execution approved</span>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Show rejected state */}
        {localApprovalState?.approved === false && (
          <div className="px-4">
            <Alert className="flex flex-col gap-2 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
              <AlertDescription className="flex items-center gap-2">
                <XIcon className="size-4 text-destructive" />
                <span>
                  Tool execution rejected: {localApprovalState.reason}
                </span>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {!showApprovalPrompt && <ToolCallOutput part={part} />}
      </ToolContent>
    </Tool>
  );
};

// Component for rendering assistant text messages
const AssistantTextPart = ({
  part,
  message,
  index,
  readonly,
  messageActionsConfig,
}: {
  part: TextPartModel;
  message: MessageClass;
  index: number;
  readonly?: boolean;
  messageActionsConfig?: MessageActionsConfig;
}) => {
  const replayAction = useAction(ReplayAction);
  const analytics = useAnalytics();
  const cocoConversationId = useCocoConversationId();
  const surface = useChatSurface() ?? 'scratch';
  const showReplay = messageActionsConfig?.showReplay !== false;

  return (
    <div key={`${message.id}-${index}`} className="group">
      <Message from="assistant">
        <MessageContent>
          <Response>{part.text}</Response>
        </MessageContent>
      </Message>
      <Actions className="-mt-4 mb-6 justify-start px-2">
        {!readonly && showReplay && (
          <Action
            onClick={() => replayAction.execute({ messageId: message.id })}
            label="Retry"
          >
            <RefreshCcwIcon className="size-3" />
          </Action>
        )}
        <Action
          onClick={() => {
            void navigator.clipboard.writeText(part.text);
            if (cocoConversationId !== null) {
              analytics.track(CocoEvent.MessageCopied, {
                conversationId: cocoConversationId,
                surface,
                role: 'assistant',
              });
            }
          }}
          label="Copy"
          size="icon"
        >
          <CopyIcon className="size-3" />
        </Action>
        <MessageTimestamp message={message} />
      </Actions>
    </div>
  );
};

// Component for rendering user text messages
const UserTextPart = ({
  part,
  message,
  index,
  userMessageIndicator,
  readonly,
  messageActionsConfig,
}: {
  part: TextPartModel;
  message: MessageClass;
  index: number;
  userMessageIndicator?: string | null;
  readonly?: boolean;
  messageActionsConfig?: MessageActionsConfig;
}) => {
  const editAction = useAction(EditAction);
  const analytics = useAnalytics();
  const cocoConversationId = useCocoConversationId();
  const surface = useChatSurface() ?? 'scratch';
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(part.text);
  const showEdit = messageActionsConfig?.showEdit !== false;

  return (
    <div key={`${message.id}-${index}`} className="group">
      <Message from="user">
        <MessageContent>
          {userMessageIndicator !== undefined &&
          userMessageIndicator !== null ? (
            <div className="mb-1 text-xs text-muted-foreground">
              {userMessageIndicator}
            </div>
          ) : null}
          {isEditing ? (
            <div className="relative">
              <Textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="min-h-24 pr-20 border-0 ring-0 focus-visible:ring-0"
                autoFocus
              />
              <div className="absolute bottom-2 right-2 flex gap-1">
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={async () => {
                    setIsEditing(false);
                    await editAction.execute({
                      messageId: message.id,
                      newMessage: value,
                    });
                  }}
                >
                  <Check className="size-3" />
                  <span className="sr-only">Confirm</span>
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => {
                    setValue(part.text);
                    setIsEditing(false);
                  }}
                >
                  <X className="size-3" />
                  <span className="sr-only">Cancel</span>
                </Button>
              </div>
            </div>
          ) : (
            <Response>{part.text}</Response>
          )}
        </MessageContent>
      </Message>
      {!isEditing && (
        <Actions className="-mt-2 mb-6 justify-end px-2">
          <MessageTimestamp message={message} />
          {!readonly && showEdit && (
            <Action onClick={() => setIsEditing(true)} label="Edit">
              <Pencil className="size-3" />
            </Action>
          )}
          <Action
            onClick={() => {
              void navigator.clipboard.writeText(part.text);
              if (cocoConversationId !== null) {
                analytics.track(CocoEvent.MessageCopied, {
                  conversationId: cocoConversationId,
                  surface,
                  role: 'user',
                });
              }
            }}
            label="Copy"
            size="icon"
          >
            <CopyIcon className="size-3" />
          </Action>
        </Actions>
      )}
    </div>
  );
};

// Component for rendering text messages with actions
const TextPart = ({
  part,
  message,
  index,
  userMessageIndicator,
  readonly,
  messageActionsConfig,
}: {
  part: TextPartModel;
  message: MessageClass;
  index: number;
  userMessageIndicator?: string | null;
  readonly?: boolean;
  messageActionsConfig?: MessageActionsConfig;
}) => {
  if (message.isAssistantMessage()) {
    return (
      <AssistantTextPart
        part={part}
        message={message}
        index={index}
        readonly={readonly}
        messageActionsConfig={messageActionsConfig}
      />
    );
  }

  if (message.isUserMessage()) {
    return (
      <UserTextPart
        part={part}
        message={message}
        index={index}
        userMessageIndicator={userMessageIndicator}
        readonly={readonly}
        messageActionsConfig={messageActionsConfig}
      />
    );
  }

  return null;
};

/**
 * Copies a user message's rich editor state to the clipboard preserving tool /
 * resource references. Writes a `text/html` payload (the mention spans) so pasting
 * back into a Coco composer reconstructs the pills, plus a clean `@label` `text/plain`
 * fallback for non-Coco targets. Falls back to plain text if the async Clipboard API
 * is unavailable. Note: this deliberately does NOT use `toPromptMarkdown`, which is the
 * LLM-prompt serializer and renders tools as `` `snake_name` (tool) ``.
 */
const copyEditorStateToClipboard = async (
  editorState: DG.DocumentEditor.DocumentState
): Promise<void> => {
  const { html, text } = serializeEditorStateForClipboard(editorState);

  try {
    if (navigator.clipboard != null && 'write' in navigator.clipboard) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      return;
    }
  } catch {
    // Fall through to the plain-text path below (e.g. permissions or unsupported type).
  }

  await navigator.clipboard.writeText(text);
};

const EditorStatePartComponent = ({
  part,
  message,
  index,
  userMessageIndicator,
  isForeignUserMessage,
  readonly,
  messageActionsConfig,
}: {
  part: InstanceType<typeof import('@cotera/sdk/core').EditorStatePart>;
  message: MessageClass;
  index: number;
  userMessageIndicator?: string | null;
  isForeignUserMessage: boolean;
  readonly?: boolean;
  messageActionsConfig?: MessageActionsConfig;
}) => {
  const editAction = useAction(EditAction);
  const analytics = useAnalytics();
  const cocoConversationId = useCocoConversationId();
  const surface = useChatSurface() ?? 'scratch';
  const showEdit = messageActionsConfig?.showEdit !== false;
  const editor = new DG.DocumentEditor.EditorState(part.editorState);
  const plainText = editor.toPromptMarkdown(DG.BuildExplorer.empty(), 'simple');

  return (
    <div key={`${message.id}-${index}`} className="group">
      <Message from="user">
        <MessageContent>
          {userMessageIndicator !== undefined &&
          userMessageIndicator !== null ? (
            <div className="mb-1 text-xs text-muted-foreground">
              {userMessageIndicator}
            </div>
          ) : null}
          <ReadonlyEditorState
            editorState={part.editorState}
            className="text-base"
            onMentionClick={messageActionsConfig?.onMentionClick}
            showMentionOwnershipIndicator={isForeignUserMessage}
            mentionOwnershipTooltipContent="This mention was added by another user."
          />
        </MessageContent>
      </Message>
      <Actions className="-mt-2 mb-6 justify-end px-2">
        <MessageTimestamp message={message} />
        {!readonly && showEdit && (
          <Action
            onClick={() =>
              editAction.execute({
                messageId: message.id,
                newMessage: plainText,
              })
            }
            label="Edit"
          >
            <Pencil className="size-3" />
          </Action>
        )}
        <Action
          onClick={() => {
            void copyEditorStateToClipboard(part.editorState);
            if (cocoConversationId !== null) {
              analytics.track(CocoEvent.MessageCopied, {
                conversationId: cocoConversationId,
                surface,
                role: 'user',
              });
            }
          }}
          label="Copy"
          size="icon"
        >
          <CopyIcon className="size-3" />
        </Action>
      </Actions>
    </div>
  );
};

const FilePart = ({
  part,
  message,
  index,
}: {
  part: FilePartModel;
  message: MessageClass;
  index: number;
}) => {
  const url = part.url;
  if (!url) {
    return null;
  }

  const filename = part.filename ?? 'attachment';
  const mediaType = part.mediaType?.toLowerCase() ?? '';
  const isImage = mediaType.startsWith('image/');
  const actionsAlignment = message.isAssistantMessage()
    ? 'justify-start'
    : 'justify-end';

  return (
    <Fragment key={`${message.id}-${index}`}>
      <Message from={message.role}>
        <MessageContent>
          {isImage ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="group block overflow-hidden rounded-lg border border-border bg-muted/20"
            >
              <img
                alt={filename}
                className="max-h-[420px] w-full object-contain transition group-hover:opacity-95"
                src={url}
              />
            </a>
          ) : (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm font-medium transition hover:bg-muted"
            >
              <PaperclipIcon className="size-4 text-muted-foreground" />
              <span className="truncate">{filename}</span>
            </a>
          )}
        </MessageContent>
      </Message>
      <Actions className={cn('-mt-2 mb-6', actionsAlignment, 'px-2')}>
        <Action
          onClick={() => {
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          label="Download"
        >
          <ArrowDownToLine className="size-3" />
        </Action>
        {isImage && (
          <Action
            onClick={() => {
              void navigator.clipboard.writeText(url);
            }}
            label="Copy URL"
            size="icon"
          >
            <CopyIcon className="size-3" />
          </Action>
        )}
      </Actions>
    </Fragment>
  );
};

// Component for rendering reasoning blocks
const ReasoningPart = ({
  part,
  message,
  index,
  chatModel,
}: {
  part: ReasoningPartModel;
  message: MessageClass;
  index: number;
  chatModel: Pick<ChatModel, 'lastMessageId' | 'status'>;
}) => {
  const status = chatModel.status;
  const isStreaming =
    status === 'streaming' &&
    part.state !== 'done' &&
    index === message.parts.length - 1 &&
    message.id === chatModel.lastMessageId;

  return (
    <Reasoning
      key={`${message.id}-${index}`}
      className="w-full"
      isStreaming={isStreaming}
    >
      <ReasoningTrigger status={part.state} />
      <ReasoningContent>{part.text}</ReasoningContent>
    </Reasoning>
  );
};

// Main render component
export const RenderMessage = ({
  message,
  chatModel,
  currentUserId,
  participantNameById,
  readonly,
  messageActionsConfig,
  customToolRenderers,
  approvalHandler,
}: {
  message: MessageClass;
  chatModel: Pick<
    ChatModel,
    'lastMessageId' | 'status' | 'addToolResult' | 'id' | 'model'
  >;
  currentUserId?: string;
  participantNameById?: Map<string, string>;
  readonly?: boolean;
  messageActionsConfig?: MessageActionsConfig;
  customToolRenderers?:
    | Record<string, CustomToolRenderer>
    | ((toolKey: string) => CustomToolRenderer | null);
  /** When provided (e.g. in sub-agent), tool approvals use this instead of ExecuteToolAction. */
  approvalHandler?: ApprovalHandler;
}) => {
  const toolParts = message.isAssistantMessage() ? message.getToolParts() : [];
  let toolPartIndex = 0;
  const raw = message.raw() as {
    metadata?: { actorId?: unknown } | null;
  };
  const actorId = raw.metadata?.actorId;
  const shouldShowIndicator =
    message.isUserMessage() &&
    typeof actorId === 'string' &&
    currentUserId !== undefined &&
    actorId !== currentUserId;
  const userMessageIndicator = shouldShowIndicator
    ? participantNameById?.get(actorId) ?? 'Unknown user'
    : null;
  const isForeignUserMessage = shouldShowIndicator;

  // Collapse runs of consecutive same-tool calls into a single grouped row.
  // Generic (registry-less) tools group by default; custom-rendered ones group
  // only when allow-listed (e.g. search). Tools awaiting approval never group so
  // they stay actionable.
  type MessagePart = (typeof message.parts)[number];
  type RenderEntry =
    | { kind: 'part'; part: MessagePart; i: number }
    | { kind: 'tool'; toolPart: ToolPart; i: number; groupable: boolean }
    | { kind: 'toolGroup'; toolKey: string; toolParts: ToolPart[]; i: number };

  const entries: RenderEntry[] = [];
  message.parts.forEach((part, i) => {
    if (part.isToolPart()) {
      const toolPart = toolParts[toolPartIndex++];
      if (!toolPart) {
        return;
      }
      const key = toolPart.toolKey().toString();
      const groupable =
        toolPart.approvalStatus() !== 'required' &&
        (resolveToolRenderer(key, customToolRenderers) === null ||
          isGroupableCustomToolKey(key));
      const last = entries[entries.length - 1];
      if (
        groupable &&
        last !== undefined &&
        ((last.kind === 'tool' &&
          last.groupable &&
          last.toolPart.toolKey().toString() === key) ||
          (last.kind === 'toolGroup' && last.toolKey === key))
      ) {
        if (last.kind === 'tool') {
          entries[entries.length - 1] = {
            kind: 'toolGroup',
            toolKey: key,
            toolParts: [last.toolPart, toolPart],
            i: last.i,
          };
        } else {
          last.toolParts.push(toolPart);
        }
      } else {
        entries.push({ kind: 'tool', toolPart, i, groupable });
      }
      return;
    }
    // Step boundaries are invisible (they render nothing), but they sit between
    // sequential tool calls — e.g. a create-agent followed by several edit-agent
    // steps. Skipping them keeps those calls "consecutive" so they still group,
    // instead of a step-start silently breaking the run.
    if (part.isStepStartPart()) {
      return;
    }
    entries.push({ kind: 'part', part, i });
  });

  return (
    <>
      <MessageSources message={message} />
      {entries.map((entry) => {
        if (entry.kind === 'toolGroup') {
          return (
            <ToolPartGroupComponent
              key={`${message.id}-group-${entry.i}`}
              toolParts={entry.toolParts}
              chatModel={chatModel}
              readonly={readonly}
              customToolRenderers={customToolRenderers}
            />
          );
        }

        if (entry.kind === 'tool') {
          return (
            <ToolPartComponent
              key={`${message.id}-${entry.i}`}
              toolPart={entry.toolPart}
              chatModel={chatModel}
              readonly={readonly}
              customToolRenderers={customToolRenderers}
              approvalHandler={approvalHandler}
            />
          );
        }

        const { part, i } = entry;

        if (part.isTextPart()) {
          return (
            <TextPart
              key={`${message.id}-${i}`}
              part={part}
              message={message}
              index={i}
              userMessageIndicator={userMessageIndicator}
              readonly={readonly}
              messageActionsConfig={messageActionsConfig}
            />
          );
        }

        if (part.isEditorStatePart()) {
          return (
            <EditorStatePartComponent
              key={`${message.id}-${i}`}
              part={part}
              message={message}
              index={i}
              userMessageIndicator={userMessageIndicator}
              isForeignUserMessage={isForeignUserMessage}
              readonly={readonly}
              messageActionsConfig={messageActionsConfig}
            />
          );
        }

        if (part.isReasoningPart()) {
          return (
            <ReasoningPart
              chatModel={chatModel}
              key={`${message.id}-${i}`}
              part={part}
              message={message}
              index={i}
            />
          );
        }

        if (part.isFilePart()) {
          return (
            <FilePart
              key={`${message.id}-${i}`}
              part={part}
              message={message}
              index={i}
            />
          );
        }

        return null;
      })}
    </>
  );
};

// --- Cross-message tool grouping -------------------------------------------
// Sequential agentic tool calls (e.g. several edit-agent steps) each land in
// their OWN assistant message — parts are just [step-start, tool] — so the
// within-message grouping in RenderMessage never sees them side by side. These
// helpers collapse a run of consecutive single-tool messages that share a key
// into one grouped row, reusing the same ToolPartGroupComponent.

// The single groupable tool of a "pure tool" message, or null. A message is a
// pure tool message when its only visible content is one groupable tool call
// (step-start parts are invisible and ignored). Any text/reasoning/file/editor
// content disqualifies it, so real narration between calls keeps runs apart.
const pureSingleGroupableTool = (
  message: MessageClass,
  customToolRenderers?:
    | Record<string, CustomToolRenderer>
    | ((toolKey: string) => CustomToolRenderer | null)
): { key: string; toolPart: ToolPart } | null => {
  if (!message.isAssistantMessage() || message.isHidden()) {
    return null;
  }
  const hasOtherVisibleContent = message.parts.some(
    (p) =>
      p.isTextPart() ||
      p.isReasoningPart() ||
      p.isFilePart() ||
      p.isEditorStatePart()
  );
  if (hasOtherVisibleContent) {
    return null;
  }
  const toolParts = message.getToolParts();
  if (toolParts.length !== 1) {
    return null;
  }
  const toolPart = toolParts[0]!;
  const key = toolPart.toolKey().toString();
  const groupable =
    toolPart.approvalStatus() !== 'required' &&
    (resolveToolRenderer(key, customToolRenderers) === null ||
      isGroupableCustomToolKey(key));
  return groupable ? { key, toolPart } : null;
};

// One entry in the rendered message list: either a normal message or a run of
// consecutive single-tool messages to collapse into one grouped row.
export type MessageRenderPlanEntry =
  | { kind: 'message'; message: MessageClass }
  | { kind: 'toolRun'; key: string; messages: MessageClass[] };

// Group runs of 2+ consecutive same-key single-tool messages. Everything else
// (and any lone tool message) passes through unchanged, so per-message chrome —
// user labels, message actions, compaction — is preserved.
export const buildMessageRenderPlan = (
  messages: MessageClass[],
  customToolRenderers?:
    | Record<string, CustomToolRenderer>
    | ((toolKey: string) => CustomToolRenderer | null)
): MessageRenderPlanEntry[] => {
  const plan: MessageRenderPlanEntry[] = [];
  for (const message of messages) {
    const single = pureSingleGroupableTool(message, customToolRenderers);
    const last = plan[plan.length - 1];
    if (
      single !== null &&
      last !== undefined &&
      last.kind === 'toolRun' &&
      last.key === single.key
    ) {
      last.messages.push(message);
      continue;
    }
    if (single !== null) {
      plan.push({ kind: 'toolRun', key: single.key, messages: [message] });
      continue;
    }
    plan.push({ kind: 'message', message });
  }
  // A run of one isn't a group — render it as an ordinary message so it keeps
  // its normal chrome.
  return plan.map((entry) =>
    entry.kind === 'toolRun' && entry.messages.length === 1
      ? { kind: 'message', message: entry.messages[0]! }
      : entry
  );
};

// Renders a cross-message run as a single grouped row, reusing the same grouped
// component the within-message path uses.
export const CrossMessageToolGroup = ({
  messages,
  chatModel,
  readonly,
  customToolRenderers,
}: {
  messages: MessageClass[];
  chatModel: Pick<ChatModel, 'addToolResult' | 'id' | 'model'>;
  readonly?: boolean;
  customToolRenderers?:
    | Record<string, CustomToolRenderer>
    | ((toolKey: string) => CustomToolRenderer | null);
}) => {
  const toolParts = messages.flatMap((m) => m.getToolParts());
  return (
    <ToolPartGroupComponent
      toolParts={toolParts}
      chatModel={chatModel}
      readonly={readonly}
      customToolRenderers={customToolRenderers}
    />
  );
};
